import { createRef, Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import axios from "axios";
import { countBy } from "lodash";
import "chart.js/auto";
import { Bar, getDatasetAtEvent } from "react-chartjs-2";
import Banner from "@shared/v2/Banner";
import Button from "@shared/v2/Button";
import Dropdown from "@shared/v2/Dropdown";
import IconButton from "@shared/v2/IconButton";
import { TriangleExclamationSolidV6, XmarkSolidV6 } from "@shared/v2/Icomoon";
import * as actions from "../../actions/personDetailActionCreators";
import "./activity.scss";
import { buildTopFive, buildQuartileData, options, sortValues } from "./ActivityHelpers";
import { BARCOLORS, COLORLABELCLASSES } from "./Colors";
import Properties from "./Properties";
import PropertyFilters from "./PropertyFilters";
import Loading from "../../../Goals/components/Loader";
import { CustomControl, CustomSingleValue } from "./CustomDropdownComponents";

function getQuartile(sortedArr, q, key) {
  const pos = (sortedArr.length - 1) * q;
  const base = Math.floor(pos);
  const rest = pos - base;
  if (sortedArr[base + 1]) {
    return sortedArr[base][key] + rest * (sortedArr[base + 1][key] - sortedArr[base][key]);
  }
  return sortedArr[base][key];
}

const ChartLegend = ({ datasets }) => (
  <ul className="leaddna-legend">
    {datasets?.map((ds) => (
      <li key={ds.label} className={`${ds.color}`} dangerouslySetInnerHTML={{ __html: ds.label }} />
    ))}
  </ul>
);

const ContactSupport = () => (
  <Banner containerClassName="tw-h-[44px]" show>
    <div className="tw-text-14d tw-flex tw-items-center tw-gap-[16px]">
      <TriangleExclamationSolidV6 size="l" className="tw-text-[#E5342E]" />
      <span className="tw-text-[#A81E19]">
        An error occurred while getting the web activity. Please try again later.
      </span>
    </div>
  </Banner>
);

ChartLegend.propTypes = {
  datasets: PropTypes.arrayOf(PropTypes.shape()).isRequired,
};

class Activity extends Component {
  constructor(props) {
    super(props);
    this.chartRef = createRef();
    this.state = {
      loading: true,
      stats: [],
      primaryFilter: "price",
      secondaryFilter: null,
      secondaryData: [],
      showSecondary: false,
      titleText: "",
      chartData: {
        labels: [],
        datasets: [],
      },
      isFavorited: props.isFavorite,
      sortBy: "most_recent",
      errorFetchingData: false,
    };
  }

  componentDidMount() {
    this.fetchData();
  }

  getTopFive(filterKey, sortedArray, usePrimaryTitle) {
    const quantities = countBy(sortedArray, filterKey);
    const arrayed = Object.keys(quantities).map((val) => ({ [val]: quantities[val] }));
    let labelDescriptor;
    let sorted;

    if (filterKey === "bedrooms" || filterKey === "bathrooms") {
      labelDescriptor = filterKey === "bedrooms" ? "bed" : "bath";
      sorted = arrayed.sort((a, b) =>
        Object.keys(a)[0] === Object.keys(b)[0] ? 0 : +(Object.keys(a)[0] > Object.keys(b)[0]) || -1,
      );
    } else {
      sorted = arrayed.sort((a, b) =>
        Object.values(b)[0] === Object.values(a)[0] ? 0 : +(Object.values(b)[0] > Object.values(a)[0]) || -1,
      );
    }

    if (usePrimaryTitle) {
      this.setState({ titleText: `Showing ${sortedArray.length} properties sorted by ${filterKey}` });
    }

    const chartData = buildTopFive(
      filterKey,
      sorted,
      sortedArray,
      labelDescriptor,
      BARCOLORS,
      COLORLABELCLASSES,
    );
    this.setState({ chartData });
  }

  sortStats = (value) => {
    const { stats } = this.state;
    const sortedStats = stats.sort((a, b) => {
      if (value === "most_recent") {
        return a.most_recent_view < b.most_recent_view ? 1 : -1;
      }
      if (value === "oldest") {
        return a.most_recent_view > b.most_recent_view ? 1 : -1;
      }
      if (value === "most_views") {
        return a.history_ext.length < b.history_ext.length ? 1 : -1;
      }
      return 0;
    });

    this.setState({ stats: sortedStats });
  };

  statsOrSpinner = (stats) => {
    const { closeModal, openAddListingAlertModal } = this.props;
    return stats.length > 0 ? (
      <Properties stats={this.filteredProperties()} />
    ) : (
      <div className="tw-flex tw-flex-col tw-gap-24px tw-items-center">
        <div className="tw-text-18d tw-font-bold">No Web Activity</div>
        <div className="tw-text-14d tw-w-[489px] tw-text-center">
          This lead has not viewed or favorited any listings on your Brivity website. You can set them up on a
          Listing Alert to increase web activity!
        </div>
        <Button
          size="medium"
          schema="primary"
          onClick={() => {
            closeModal();
            openAddListingAlertModal();
          }}
        >
          Create Listing Alert
        </Button>
      </div>
    );
  };

  closeSecondary = () => {
    const { primaryFilter, titleText } = this.state;
    this.setState({ showSecondary: false, titleText });
    this.filterPrimary(primaryFilter);
  };

  toggleButtons = () => {
    this.setState((prevState) => ({ isFavorited: !prevState.isFavorited }));
  };

  filteredProperties = () => {
    const { isFavorited, stats } = this.state;
    if (isFavorited) {
      return stats.filter((property) => property.favorite === isFavorited);
    }
    return stats;
  };

  favoriteCount = () => {
    const { stats } = this.state;
    return stats.filter((property) => property.favorite === true).length;
  };

  handleSelectSortBy = (option) => {
    this.setState({ sortBy: option.value }, () => {
      this.sortStats(option.value);
    });
  };

  buildSecondaryData(secondaryData, secondaryKey, title) {
    this.setState({ secondaryData, secondaryFilter: secondaryKey, showSecondary: true, titleText: title });
    this.buildDatasets(secondaryKey, secondaryData, false);
  }

  filterPrimary(primaryKey) {
    const { stats, titleText, sortBy } = this.state;
    this.setState({ primaryFilter: primaryKey });
    this.buildDatasets(primaryKey, stats, titleText, true);
    this.sortStats(sortBy);
  }

  filterSecondary(secondaryKey) {
    const { secondaryData, titleText } = this.state;
    this.buildSecondaryData(secondaryData, secondaryKey, titleText);
  }

  determineInitialSecondary() {
    const { primaryFilter } = this.state;
    if (primaryFilter === "price") return "bedrooms";
    return "price";
  }

  startSecondaryData(secondaryData, secondaryKey, title) {
    const { showSecondary } = this.state;
    if (!showSecondary) this.buildSecondaryData(secondaryData, secondaryKey, title);
  }

  quartileRanges(filterKey, sortedArray, usePrimaryTitle) {
    if (usePrimaryTitle) {
      this.setState({ titleText: `Showing ${sortedArray.length} properties in all price ranges` });
    }

    if (sortedArray.length > 6) {
      // Always sort the array by filterKey
      const arr = [...sortedArray].sort((a, b) => a[filterKey] - b[filterKey]);

      // Calculate quartiles using interpolation
      const q1 = getQuartile(arr, 0.25, filterKey);
      const q3 = getQuartile(arr, 0.75, filterKey);

      const iqr = q3 - q1;
      const maxAllowable = q3 + iqr * 1.5;
      const minAllowable = q1 - iqr * 1.5;

      // Partition into normal and outliers
      const normalAndOutliers = [[], []];
      arr.forEach((value) => {
        if (value[filterKey] >= minAllowable && value[filterKey] <= maxAllowable) {
          normalAndOutliers[0].push(value);
        } else {
          normalAndOutliers[1].push(value);
        }
      });

      const rangeMultiplier = (maxAllowable - minAllowable) / 5;

      const chartData = buildQuartileData(
        filterKey,
        normalAndOutliers,
        rangeMultiplier,
        BARCOLORS,
        COLORLABELCLASSES,
      );
      this.setState({ chartData });
    } else {
      this.getTopFive(filterKey, sortedArray, usePrimaryTitle);
    }
  }

  buildDatasets(filterKey, data, usePrimaryTitle) {
    const sortedArray = sortValues(filterKey, data);

    if (filterKey === "price") {
      this.quartileRanges(filterKey, sortedArray, usePrimaryTitle);
    } else {
      this.getTopFive(filterKey, sortedArray, usePrimaryTitle);
    }
  }

  fetchData() {
    const { uuid, views } = this.props;
    const { primaryFilter, sortBy } = this.state;
    axios
      .get(`/idx_data/property_stats/${uuid}`)
      .then((res) => {
        this.setState(
          {
            loading: false,
            stats: res.data.stats,
            errorFetchingData: res.data.stats.length === 0 && views > 0,
          },
          () => {
            this.buildDatasets(primaryFilter, res.data.stats, true);
            this.sortStats(sortBy);
          },
        );
      })
      .catch(() => {
        this.setState({ loading: false, errorFetchingData: true, stats: [] });
      });
  }

  render() {
    const {
      chartData,
      errorFetchingData,
      isFavorited,
      loading,
      primaryFilter,
      secondaryFilter,
      showSecondary,
      sortBy,
      stats,
      titleText,
    } = this.state;

    const filterValues = [
      {
        label: "Price",
        value: "price",
        disabled: primaryFilter === "price" && showSecondary === true,
      },
      {
        label: "Beds",
        value: "bedrooms",
        disabled: primaryFilter === "bedrooms" && showSecondary === true,
      },
      {
        label: "Baths",
        value: "bathrooms",
        disabled: primaryFilter === "bathrooms" && showSecondary === true,
      },
      {
        label: "City",
        value: "city",
        disabled: primaryFilter === "city" && showSecondary === true,
      },
      {
        label: "Zip Code",
        value: "zip",
        disabled: primaryFilter === "zip" && showSecondary === true,
      },
    ];

    return (
      <div id="lead-dna-details" className="tw-flex tw-flex-col tw-gap-[24px]">
        <div className="tw-flex tw-items-center">
          <div className="tw-basis-4/12 tw-flex tw-items-center tw-gap-[8px]">
            <span className="tw-text-11d">Show distribution by</span>
            <Dropdown
              components={{ Control: CustomControl, SingleValue: CustomSingleValue }}
              value={filterValues.find((o) => o.value === primaryFilter)}
              options={filterValues}
              onChange={(option) => this.filterPrimary(option.value)}
              menuShouldComeToFront
            />
          </div>
          <div className="tw-basis-8/12 tw-text-12d tw-font-bold">{titleText}</div>
        </div>

        {showSecondary && (
          <div className="tw-flex tw-items-center tw-gap-[8px]">
            <span className="tw-text-11d">Of that, show by: </span>
            <div className="tw-flex tw-items-center tw-gap-[4px]">
              <IconButton size="small" schema="transparent" onClick={this.closeSecondary}>
                <XmarkSolidV6 />
              </IconButton>
              <Dropdown
                containerClassName="tw-w-[120px]"
                value={filterValues.find((o) => o.value === secondaryFilter)}
                options={filterValues}
                onChange={(option) => this.filterSecondary(option.value)}
                menuShouldComeToFront
              />
            </div>
          </div>
        )}

        {stats.length > 0 ? (
          <div>
            <div className="tw-ml-[-8px]">
              <Bar
                ref={this.chartRef}
                height={40}
                data={chartData}
                options={options(this.state)}
                onClick={(event) => {
                  if (!this.chartRef.current) return;
                  const dataset = getDatasetAtEvent(this.chartRef.current, event);
                  this.startSecondaryData(
                    chartData.datasets[dataset[0].datasetIndex].metaCollection,
                    this.determineInitialSecondary(),
                    chartData.datasets[dataset[0].datasetIndex].secondaryTitle,
                  );
                }}
              />
            </div>
            <ChartLegend datasets={chartData.datasets} />
          </div>
        ) : null}

        <PropertyFilters
          isFavorited={isFavorited}
          toggleButtons={this.toggleButtons}
          allViewCount={stats.length}
          favoriteCount={this.favoriteCount()}
          sortBy={sortBy}
          handleSelectSortBy={this.handleSelectSortBy}
        />

        <div className="tw-bg-gray-10 tw-h-[1px]" />

        {loading && <Loading />}
        {!loading && errorFetchingData && <ContactSupport />}
        {!loading && !errorFetchingData && this.statsOrSpinner(stats)}
      </div>
    );
  }
}

Activity.propTypes = {
  uuid: PropTypes.string.isRequired,
  views: PropTypes.number.isRequired,
  isFavorite: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  openAddListingAlertModal: PropTypes.func.isRequired,
};

export default connect(null, { openAddListingAlertModal: actions.openAddListingAlertModal })(Activity);
